import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "../../../../../scss/components/_ag-table.scss";
import { getServerSideDatasourcePPC } from "@revbox/helpers-fe";
import { brandingPPCAuctionsLabels, searchPlaceholder } from "@revbox/localization";
import "ag-grid-charts-enterprise";
import {
  PositionRenderer,
  PositionCellTooltipRenderer,
  PositionColumnHeaderTooltipRenderer,
} from "./BrandingPositionRenderer";
import { StatusCellTooltipRenderer, StatusRenderer, StatusColumnHeaderTooltipRenderer } from "./BrandingStatusRenderer";
import {
  YourBidCellColumnHeaderTooltipRenderer,
  YourBidCellRenderer,
  YourBidCellTooltipRenderer,
} from "./BrandingYourBidRenderer";
import { CustomTooltip } from "../../common/CustomTooltip";
import { BrandingNameCellTooltipRenderer, BrandingNameColumnHeaderTooltipRenderer, BrandingNameRenderer } from "./BrandingNameRenderer";
import { TopBidCellColumnHeaderTooltipRenderer, TopBidCellRenderer, TopBidCellTooltipRenderer } from "./BrandingTopBidRenderer";
import { ClicksCellRenderer, YourClicksCellColumnHeaderTooltipRenderer, YourClicksCellTooltipRenderer } from "./BrandingClicksRenderer";
import { ActionCellColumnHeaderTooltipRenderer, ActionCellTooltipRenderer, ActionRenderer } from "./BrandingActionRenderer";
import { MissedClicksForecastCellColumnHeaderTooltipRenderer, MissedClicksForecastCellTooltipRenderer, MissedClicksForecastRenderer } from "./BrandingMissedClicksForecastRenderer";
import { useCookiesContext } from "../../../../../contexts/CookiesContext";

const customColumns = [
  {
    field: "Name",
    cellRenderer: BrandingNameRenderer,
    headerTooltip: BrandingNameColumnHeaderTooltipRenderer(),
    tooltipValueGetter: BrandingNameCellTooltipRenderer
  },
  {
    field: "Position",
    cellRenderer: PositionRenderer,
    headerTooltip: PositionColumnHeaderTooltipRenderer(),
    tooltipValueGetter: PositionCellTooltipRenderer,
  },
  {
    field: "Status",
    cellRenderer: StatusRenderer,
    headerTooltip: StatusColumnHeaderTooltipRenderer(),
    tooltipValueGetter: StatusCellTooltipRenderer,
  },
  {
    field: "Your Bid",
    cellRenderer: YourBidCellRenderer,
    headerTooltip: YourBidCellColumnHeaderTooltipRenderer(),
    tooltipValueGetter: YourBidCellTooltipRenderer
  },
  {
    field: "Top Bid",
    cellRenderer: TopBidCellRenderer,
    headerTooltip: TopBidCellColumnHeaderTooltipRenderer(),
    tooltipValueGetter: TopBidCellTooltipRenderer
  },
  {
    field: "Your Clicks",
    cellRenderer: ClicksCellRenderer,
    headerTooltip: YourClicksCellColumnHeaderTooltipRenderer(),
    tooltipValueGetter: YourClicksCellTooltipRenderer,
  },
  {
    field: "Action",
    cellRenderer: ActionRenderer,
    headerTooltip: ActionCellColumnHeaderTooltipRenderer(),
    tooltipValueGetter: ActionCellTooltipRenderer
  },
  {
    field: "Missed Clicks Forecast",
    cellRenderer: MissedClicksForecastRenderer,
    headerTooltip: MissedClicksForecastCellColumnHeaderTooltipRenderer(),
    tooltipValueGetter: MissedClicksForecastCellTooltipRenderer,
  },
];

export const BrandingPPCAuctions = ({
  getData,
  skin,
  onClick,
  campaignId,
  updatePage,
  isHTR,
  setBrandingActiveAuctions,
  setAnyBrandingAuctions,
  setAnyActiveBrandingBid,
  display
}) => {
  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
  const gridStyle = useMemo(() => ({ minHeight: "200px", width: "100%" }), []);
  
  const [search, setSearch] = useState("");
  const [gridApi, setGridApi] = useState(null);
  const [columnDefs, setColumnDefs] = useState([]);
  const [data, setData] = useState([]);
  const { getLanguageAbbr } = useCookiesContext();
  const languageAbbr = getLanguageAbbr();
  const multilang = brandingPPCAuctionsLabels[languageAbbr];
  const search_placeholder = searchPlaceholder[languageAbbr];
  useEffect(() => {
    getData({}, campaignId).then((res) => {
      setData(res.data);

      if (res.data.rows.length > 0) {
        const activeAuctions = res.data.rows.filter(item => item[2] === 'Live');
        setBrandingActiveAuctions(activeAuctions);
        setAnyBrandingAuctions(true);
        const anyActiveBidFlag = activeAuctions.some(row => row[2] === "Live" || row[2] === "Paused" || row[2] === "Outbid");
        setAnyActiveBrandingBid(anyActiveBidFlag);
      }
    });
    // updatePage is used to refresh the tabs when the page is updated
  }, [updatePage]);

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 90,
      tooltipComponent: CustomTooltip,
    };
  }, []);

  const onFilterTextBoxChanged = (e) => {
    setSearch(e.target.value);
  };

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
    const datasource = getServerSideDatasourcePPC(
      getData,
      setColumnDefs,
      search,
      customColumns,
      campaignId,
      languageAbbr
    );

    params.api.setGridOption("serverSideDatasource", datasource);
  }, []);

  useEffect(() => {
    if (gridApi) {
      const datasource = getServerSideDatasourcePPC(
        getData,
        setColumnDefs,
        search,
        customColumns,
        campaignId,
        languageAbbr
      );

      gridApi.setGridOption("serverSideDatasource", datasource);
    }
  }, [gridApi, search, updatePage, campaignId]);

  if (display !== "branding" || data?.rows?.length === 0) {
    return <></>;
  }

  return (
    <Card className="card-one">
      <Card.Header className="d-flex flex-column align-items-start">
        <Card.Title as="h6" className="mb-2">
          {multilang.title}
        </Card.Title>
        { isHTR &&
          <Card.Subtitle as="p">
            {multilang.subtitle}
            <Card.Link
              target="_blank"
              className="ps-2"
              href="https://help.hoteltechreport.com/en/collections/10828721-auction-types"
            >
              {multilang.learnMore}
            </Card.Link>
          </Card.Subtitle>
        }
      </Card.Header>
      <Card.Body>
        <div style={containerStyle}>
          <div
            style={gridStyle}
            className={
              skin === "dark" ? "ag-theme-quartz-dark ag-theme-quartz" : "ag-theme-quartz-light ag-theme-quartz"
            }
          >
            <div style={{ marginBottom: "10px" }}>
              <input
                type="text"
                className="input-search"
                placeholder={search_placeholder}
                onInput={onFilterTextBoxChanged}
              />
            </div>
            <AgGridReact
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={true}
              tooltipShowDelay={500}
              tooltipHideDelay={200000}
              paginationPageSize={10}
              rowHeight={60}
              paginationPageSizeSelector={false}
              cacheBlockSize={10}
              rowModelType={"serverSide"}
              domLayout={"autoHeight"}
              suppressContextMenu={true}
              onCellClicked={onClick}
            />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};
